import React from 'react';
import {
    Card,
    CardContent,
    Grid,
    Box,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import DashboardPanelHeader from '../components/DashboardPanelHeader';
import type { DashboardExportParams } from '../utils/dashboardExportApi';
import SessionEngagementChart from './SessionEngagementChart';
import {
    DurationBucketsChart,
    EngagementHeatmapChart,
    EngagementRateTrendChart,
    LandingPageQualityChart,
    TrafficSourceQualityChart,
} from './SessionInsightCharts';

interface PageStat {
    page_path: string;
    page_label?: string;
    category_name?: string | null;
    sessions: number;
}

interface SessionQualityData {
    entrance_pages?: PageStat[];
    exit_pages?: PageStat[];
    active_vs_idle?: { active_sessions: number; idle_sessions: number };
    landing_page_quality?: Array<{
        page_path?: string;
        page_label?: string;
        sessions?: number;
        engaged_sessions?: number;
        low_engagement_sessions?: number;
        engagement_rate_percent?: number;
    }>;
    traffic_source_quality?: Array<{
        source_name?: string;
        medium_name?: string;
        sessions?: number;
        engaged_sessions?: number;
        low_engagement_sessions?: number;
        engagement_rate_percent?: number;
    }>;
    duration_buckets?: Array<{
        duration_bucket?: string;
        sessions?: number;
        engaged_sessions?: number;
        low_engagement_sessions?: number;
    }>;
    engagement_heatmap?: Array<{
        day_of_week?: number;
        hour_of_day?: number;
        sessions?: number;
        engaged_sessions?: number;
        engagement_rate_percent?: number;
    }>;
}

interface Props {
    data?: SessionQualityData | null;
    trendData?: Array<{
        period?: string;
        period_start?: string;
        engaged_sessions?: number;
        low_engagement_sessions?: number;
        sessions?: number;
        bounce_rate?: number;
        avg_pages?: number;
        avg_duration_seconds?: number;
    }>;
    period: 'daily' | 'weekly' | 'monthly' | 'quarterly';
    loading?: boolean;
    exportParams?: Omit<DashboardExportParams, 'panel'>;
}

const renderTable = (items?: PageStat[]) => {
    if (!items || items.length === 0) {
        return <Box sx={{ height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>No data</Box>;
    }
    return (
        <TableContainer>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Page</TableCell>
                        <TableCell align="right">Sessions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
            {items.map((item, idx) => (
                <TableRow key={`${item.page_path}-${idx}`}>
                    <TableCell>{item.page_label || item.page_path || '/'}</TableCell>
                    <TableCell align="right">{item.sessions}</TableCell>
                </TableRow>
            ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const SessionQualityCards: React.FC<Props> = ({ data, trendData = [], period, loading, exportParams }) => {
    return (
        <Grid container spacing={2} sx={{ marginTop: 3 }}>
            <Grid item xs={12}>
                <Card>
                    <DashboardPanelHeader
                        title="Session Engagement"
                        subheader="Compares useful visits with short visits for each selected time period. A session is counted as engaged when the visitor viewed 2 or more pages, or stayed at least 3 minutes. Low engagement means the visitor had a short one-page session."
                        exportFilename="session-engagement-detail"
                        exportPanel="sessions_detail"
                        exportParams={exportParams}
                    />
                    <CardContent>
                        {loading ? (
                            <Skeleton height={380} />
                        ) : (
                            <SessionEngagementChart data={trendData} period={period} />
                        )}
                    </CardContent>
                </Card>
            </Grid>
            <Grid item xs={12}>
                <Card>
                    <DashboardPanelHeader
                        title="Engagement Rate Trend"
                        subheader="Shows what percentage of sessions were engaged in each time period. Engagement rate means engaged sessions divided by total sessions, so it helps compare quality even when traffic volume changes."
                        exportFilename="engagement-rate-detail"
                        exportPanel="sessions_detail"
                        exportParams={exportParams}
                    />
                    <CardContent>
                        {loading ? (
                            <Skeleton height={340} />
                        ) : (
                            <EngagementRateTrendChart data={trendData} period={period} />
                        )}
                    </CardContent>
                </Card>
            </Grid>
            <Grid item xs={12} md={6}>
                <Card>
                    <DashboardPanelHeader
                        title="Landing Page Quality"
                        subheader="Shows which pages visitors started on and how often those visits became engaged. Engagement rate means the percentage of sessions from that landing page where the visitor viewed 2 or more pages, or stayed at least 3 minutes."
                        exportFilename="landing-page-quality-detail"
                        exportPanel="sessions_detail"
                        exportParams={exportParams}
                    />
                    <CardContent>
                        {loading ? (
                            <Skeleton height={360} />
                        ) : (
                            <LandingPageQualityChart data={data?.landing_page_quality || []} />
                        )}
                    </CardContent>
                </Card>
            </Grid>
            <Grid item xs={12} md={6}>
                <Card>
                    <DashboardPanelHeader
                        title="Traffic Source Quality"
                        subheader="Shows which traffic sources bring better visitors. Each source is split into engaged sessions and low-engagement sessions, where engaged means 2 or more pages viewed, or at least 3 minutes spent."
                        exportFilename="traffic-source-quality-detail"
                        exportPanel="sessions_detail"
                        exportParams={exportParams}
                    />
                    <CardContent>
                        {loading ? (
                            <Skeleton height={360} />
                        ) : (
                            <TrafficSourceQualityChart data={data?.traffic_source_quality || []} />
                        )}
                    </CardContent>
                </Card>
            </Grid>
            <Grid item xs={12} md={6}>
                <Card>
                    <DashboardPanelHeader
                        title="Session Duration Buckets"
                        subheader="Groups sessions by how long visitors stayed. This helps show whether most visits are very short, medium length, or long enough to suggest stronger interest."
                        exportFilename="session-duration-buckets-detail"
                        exportPanel="sessions_detail"
                        exportParams={exportParams}
                    />
                    <CardContent>
                        {loading ? (
                            <Skeleton height={340} />
                        ) : (
                            <DurationBucketsChart data={data?.duration_buckets || []} />
                        )}
                    </CardContent>
                </Card>
            </Grid>
            <Grid item xs={12} md={6}>
                <Card>
                    <DashboardPanelHeader
                        title="Day / Hour Engagement"
                        subheader="Shows the days and hours when visits are most useful. Darker cells mean a higher percentage of sessions in that time slot were engaged."
                        exportFilename="day-hour-engagement-detail"
                        exportPanel="sessions_detail"
                        exportParams={exportParams}
                    />
                    <CardContent>
                        {loading ? (
                            <Skeleton height={360} />
                        ) : (
                            <EngagementHeatmapChart data={data?.engagement_heatmap || []} />
                        )}
                    </CardContent>
                </Card>
            </Grid>
            <Grid item xs={12} md={6}>
                <Card>
                    <DashboardPanelHeader
                        title="Top Entrance Pages"
                        subheader="Pages where visitors most often began their visit during the selected date range."
                        exportFilename="top-entrance-pages-detail"
                        exportPanel="sessions_detail"
                        exportParams={exportParams}
                    />
                    <CardContent>
                        {loading ? <Skeleton height={200} /> : renderTable(data?.entrance_pages)}
                    </CardContent>
                </Card>
            </Grid>
            <Grid item xs={12} md={6}>
                <Card>
                    <DashboardPanelHeader
                        title="Top Exit Pages"
                        subheader="Pages where visitors most often left the website during the selected date range."
                        exportFilename="top-exit-pages-detail"
                        exportPanel="sessions_detail"
                        exportParams={exportParams}
                    />
                    <CardContent>
                        {loading ? <Skeleton height={200} /> : renderTable(data?.exit_pages)}
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );
};

export default SessionQualityCards;
